import { ChainId, SUPPORTED_CHAIN_IDS, NetworkName } from "./networks"


// ------------------------------------------------------------
// Sonic Testnet
// ------------------------------------------------------------


// Protocol configuration for each chain
export interface ProtocolConfig {
  // Core protocol contracts
  controller: string
  veUSD: string
  vaultManager: string
  borrowerOperations: string
  pegStabilityModule: string
  sortedVaults: string
  hintHelpers: string

  // Pool contracts
  pools: {
    activePool: string
    defaultPool: string
    stabilityPool: string
    collSurplusPool: string
  }

  // Risk management contracts
  riskManagement: {
    debtCapManager: string
    debtCapLens: string
  }

  // Collateral tokens with their specific oracles
  collaterals: Array<{
    symbol: string
    address: string
    decimals: number
    priceFeed: string // oracle specific to this collateral
    feeCurve: string
    router: string
  }>
}

// Zero address constant
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as const

// Protocol addresses by chain ID - Generated from deployment files
export const addresses: Record<ChainId, ProtocolConfig> = {
  "57054": {
    "controller": "0x83e767B100B7c4e22871a58B3a6E8eDd5359496E",
    "veUSD": "0xed6E002E6DB4A0E083c97aEcCC25201a3e83dA43",
    "vaultManager": "0x5f5A5c9aA4C781972D8Fd417138CdcfC9874b73B",
    "borrowerOperations": "0x2D9F3098f5BE5f60f6Dba98adb9e00D290817413",
    "pegStabilityModule": "0x0000000000000000000000000000000000000000",
    "sortedVaults": "0xcfc5E386Db055C7B819741aa4f1224518f468745",
    "hintHelpers": "0xCddDF9D1489A3BDe23BFDB6149E65CE3C8563c44",
    "pools": {
      "activePool": "0xeCECE7cb771de842Ad06012087F384d33251B77b",
      "defaultPool": "0xE19173d56F212A40C5BBC2070b91cdD175655470",
      "stabilityPool": "0x88c226601868DfCD1ADac6fA54c7Ee85529a1440",
      "collSurplusPool": "0x7570b69502061f8B4dD98e7ADB50DeB1583224Db"
    },
    "riskManagement": {
      "debtCapManager": "0x59a49B158394694bcC2EF94dC1d2EA0086d54234",
      "debtCapLens": "0x368A5eAc605C2E62122F753Ec58A526b125e1E69"
    },
    "collaterals": []
  },
  "59141": {
    "controller": "0x0000000000000000000000000000000000000000",
    "veUSD": "0x0000000000000000000000000000000000000000",
    "vaultManager": "0x0000000000000000000000000000000000000000",
    "borrowerOperations": "0x0000000000000000000000000000000000000000",
    "pegStabilityModule": "0x0000000000000000000000000000000000000000",
    "sortedVaults": "0x0000000000000000000000000000000000000000",
    "hintHelpers": "0x0000000000000000000000000000000000000000",
    "pools": {
      "activePool": "0x0000000000000000000000000000000000000000",
      "defaultPool": "0x0000000000000000000000000000000000000000",
      "stabilityPool": "0x0000000000000000000000000000000000000000",
      "collSurplusPool": "0x0000000000000000000000000000000000000000"
    },
    "riskManagement": {
      "debtCapManager": "0x0000000000000000000000000000000000000000",
      "debtCapLens": "0x0000000000000000000000000000000000000000"
    },
    "collaterals": []
  }
}

// Helper functions
export function getConfigForChainId(chainId: ChainId): ProtocolConfig {
  const config = addresses[chainId]
  if (!config) {
    throw new Error(`No protocol configuration found for chain ID ${chainId}`)
  }
  return config
}

export function getConfigByName(networkName: NetworkName): ProtocolConfig {
  const chainId = SUPPORTED_CHAIN_IDS[networkName]
  return getConfigForChainId(chainId)
}

// Chain ID to network name mapping
export function getNetworkName(chainId: ChainId): NetworkName {
  const entry = Object.entries(SUPPORTED_CHAIN_IDS).find(([, id]) => id === chainId)
  if (!entry) {
    throw new Error(`No network name found for chain ID ${chainId}`)
  }
  return entry[0] as NetworkName
}

// Check if chain is supported
export function isSupportedChain(chainId: number): chainId is ChainId {
  return Object.values(SUPPORTED_CHAIN_IDS).includes(chainId as ChainId)
}